import React, { useEffect, useState } from 'react';
import { NAICS_CODES } from '../../data/naicsCodes';

/**
 * A band of what the government actually bought in the last two weeks,
 * from SAM.gov award notices: who won, how much, for what. It drifts
 * right-to-left slowly enough to read, pauses under the pointer or
 * keyboard focus, and does not move at all when the OS asks for reduced
 * motion (then it is a row you scroll). Nothing here is typed in; if the
 * feed cannot be fetched the band is not shown.
 */
interface FeedItem { dept: string; sub: string | null; title: string; naics: string | null; setAside: string | null; amount: number; date: string; awardee: string; state: string | null }
interface Feed { window_days: number; awards: number; total: number; agencies: number; small_business_awards: number; small_business_total: number; items: FeedItem[] }

const NAICS_NAME = new Map(NAICS_CODES.map((n) => [n.code, n.description]));
const SMALL = new Set(['of', 'the', 'and', 'for', 'de']);
const titleCase = (s: string) => s.toLowerCase().replace(/(^|[\s(\-/&.])([a-z])/g, (m, pre, c) => pre + c.toUpperCase()).replace(/\b(Llc|Inc|Ltd|Lp|Pc|Jv|Usa)\b\.?/g, (m) => m.toUpperCase());
const money = (n: number) => n >= 1e9 ? `$${(n / 1e9).toFixed(1)}B` : n >= 1e6 ? `$${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M` : n >= 1e3 ? `$${Math.round(n / 1e3)}K` : `$${Math.round(n)}`;
/** "VETERANS AFFAIRS, DEPARTMENT OF" → "Dept. of Veterans Affairs"; "DEPT OF THE AIR FORCE" → "Air Force". */
function agencyLabel(dept: string, sub: string | null): string {
  const raw = (sub || dept || '').trim();
  const inverted = raw.replace(/^(.+),\s+(DEPARTMENT|DEPT) OF$/i, 'DEPT OF $1');
  const words = inverted.toLowerCase().replace(/\bdept\b/g, 'department').split(/\s+/).map((w, i) => (i > 0 && SMALL.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1))).join(' ');
  return words.replace(/^Department of (the )?/i, '').replace(/^U\.s\. /, 'U.S. ');
}
const SET_ASIDE: Record<string, string> = { SBA: 'Small business', SBP: 'Small business', '8A': '8(a)', '8AN': '8(a)', SDVOSBC: 'SDVOSB', SDVOSBS: 'SDVOSB', WOSB: 'WOSB', EDWOSB: 'EDWOSB', HZC: 'HUBZone', HZS: 'HUBZone', VSA: 'Veteran-owned', VSS: 'Veteran-owned', IEE: 'Indian economic enterprise', ISBEE: 'Indian small business' };

const AwardsFeed: React.FC = () => {
  const [feed, setFeed] = useState<Feed | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/v1/public/awards-feed')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d && Array.isArray(d.items) && d.items.length >= 6) setFeed(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!feed) return null;

  const Item: React.FC<{ it: FeedItem; hidden?: boolean }> = ({ it, hidden }) => (
    <li aria-hidden={hidden || undefined} className="flex w-[21rem] flex-none flex-col gap-1 rounded-token border border-border bg-surface px-4 py-3 text-left shadow-e1">
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate text-sm font-semibold text-content">{titleCase(it.awardee)}</span>
        <span className="flex-none text-sm font-semibold tabular-nums text-content">{money(it.amount)}</span>
      </div>
      <div className="truncate text-xs text-content-muted">{agencyLabel(it.dept, it.sub)}{it.state ? ` · ${it.state}` : ''}</div>
      <div className="truncate text-xs text-content-muted" title={it.title}>{it.naics && NAICS_NAME.get(it.naics) ? NAICS_NAME.get(it.naics) : it.title}</div>
      {it.setAside && SET_ASIDE[it.setAside.toUpperCase()] && <span className="mt-0.5 w-fit rounded-full bg-accent-sunk px-2 py-0.5 text-[11px] font-medium text-accent">{SET_ASIDE[it.setAside.toUpperCase()]} set-aside</span>}
    </li>
  );

  // The track holds the list twice so the loop has no seam; the second copy
  // is hidden from assistive tech. Duration scales with length so the pace
  // stays readable whether there are twelve items or twenty-four.
  const seconds = Math.max(40, feed.items.length * 4);
  return (
    <section aria-label="Recent federal awards" className="border-b border-border bg-surface-sunk">
      <style>{`
        @keyframes clevor-feed { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .clevor-feed-track { animation: clevor-feed ${seconds}s linear infinite; }
        .clevor-feed:hover .clevor-feed-track, .clevor-feed:focus-within .clevor-feed-track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .clevor-feed-track { animation: none; } .clevor-feed { overflow-x: auto; scroll-snap-type: x proximity; } }
      `}</style>
      <div className="mx-auto max-w-7xl px-6 pt-8 lg:px-8">
        <p className="text-sm text-content-muted">
          <span className="font-semibold text-content">Awarded in the last {feed.window_days} days:</span>{' '}
          {feed.awards.toLocaleString()} federal contracts across {feed.agencies} departments.{' '}
          {feed.small_business_awards > 0 && <>{feed.small_business_awards.toLocaleString()} went to small-business set-asides, {money(feed.small_business_total)} in all.</>}
          <span className="text-content-subtle"> From SAM.gov award notices, as posted.</span>
        </p>
      </div>
      <div className="clevor-feed relative mt-4 overflow-hidden pb-8" style={{ maskImage: 'linear-gradient(to right, transparent, black 4%, black 96%, transparent)', WebkitMaskImage: 'linear-gradient(to right, transparent, black 4%, black 96%, transparent)' }}>
        <ul className="clevor-feed-track flex w-max gap-3 px-6">
          {feed.items.map((it, i) => <Item key={`a${i}`} it={it} />)}
          {feed.items.map((it, i) => <Item key={`b${i}`} it={it} hidden />)}
        </ul>
      </div>
    </section>
  );
};

export default AwardsFeed;
